import { useTheme } from "@emotion/react";
import { Link } from "react-router-dom-v5-compat";

import { navAccent, navIconBg } from "../nav/navConstants";
import { isSignalGroupUnread, SignalDocumentGroup } from "./groupSignals";
import { signalHref } from "./signalHref";
import { SignalLabelsList } from "./SignalLabelsList";
import { signalColor, signalLevelLabel, topSignalLevel } from "./signalLevel";

type Props = {
  group: SignalDocumentGroup;
  onNavigate?: () => void;
  onMarkRead?: (group: SignalDocumentGroup) => void;
};

export const SignalGroupRow: React.FC<Props> = ({ group, onNavigate, onMarkRead }) => {
  const theme = useTheme();
  const primarySignal = group.signals[0];
  const href = signalHref(primarySignal);
  const level = topSignalLevel(group.signals);
  const isUnread = isSignalGroupUnread(group);

  const rowCss = {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    padding: "0.375rem 0.5rem",
    borderRadius: theme.smallBorderRadius,
    textDecoration: "none",
    color: navAccent,
    opacity: isUnread ? 1 : 0.65,
    "&:hover": { background: "rgba(0, 0, 0, 0.03)" },
  };

  const content = (
    <>
      <div css={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "0.125rem" }}>
        <span css={{ display: "flex", alignItems: "center", gap: "0.375rem", minWidth: 0 }}>
          <span css={{ fontSize: "0.875rem", fontWeight: isUnread ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {group.name}
          </span>
        </span>
        <SignalLabelsList group={group} color={navAccent} readOpacity={0.75} />
      </div>
      <span
        css={{
          flexShrink: 0,
          fontSize: "0.75rem",
          fontWeight: 500,
          padding: "0.125rem 0.5rem",
          borderRadius: theme.smallBorderRadius,
          background: navIconBg,
          color: signalColor(level, theme),
          whiteSpace: "nowrap",
        }}
      >
        {signalLevelLabel[level]}
      </span>
    </>
  );

  if (!href) return <div css={rowCss}>{content}</div>;

  return (
    <Link
      to={href}
      onClick={() => {
        onMarkRead?.(group);
        onNavigate?.();
      }}
      css={rowCss}
    >
      {content}
    </Link>
  );
};
